import SelectListAnswer from '@/components/shared/answer-types/select-list/select-list.vue';
import NumberAnswer from '@/components/shared/answer-types/number/number.vue';
import TextAnswer from '@/components/shared/answer-types/text/text.vue';
import {
    MAX_MEMBERSHIP_PRICE,
    MIN_MEMBERSHIP_PRICE,
    MEMBERSHIP_PRICE_PRECISION,
    DEFAULTS,
    buildMembershipTypeRemovalWarning
} from '../register-club/constants/club-registration-constants.js';
import { EventBus } from '@/services/Event.js';
import ResourceSvc from '@/services/resource.svc.js';
import { clubBackRouteMixin } from '../mixins/club-back-route.js';
import ConfirmDialog from '@/components/shared/confirm-dialog/confirm-dialog.vue';
const svc = new ResourceSvc();

export default {
    name: 'ClubMembershipTypes',
    mixins: [clubBackRouteMixin],
    components: {
        'select-list-answer': SelectListAnswer,
        'number-answer': NumberAnswer,
        'text-answer': TextAnswer,
        'confirm-dialog': ConfirmDialog
    },
    data() {
        return {
            clubId: null,
            approvedTypes: [],
            requestedTypes: [],
            availableTypes: [],
            selectedTypeId: null,
            newPrice: null,
            newMembershipTypeName: '',
            isLoading: false,
            isSaving: false,
            savedSuccessfully: false,
            isRemovalWarningVisible: false,
            removalWarning: null,
            maxPrice: MAX_MEMBERSHIP_PRICE,
            minPrice: MIN_MEMBERSHIP_PRICE,
            pricePrecision: MEMBERSHIP_PRICE_PRECISION,
            defaults: DEFAULTS
        };
    },
    computed: {
        availableToAdd() {
            const assignedIds = this.requestedTypes.map(r => r.clubMembershipTypeId);
            return this.availableTypes.filter(t => !assignedIds.includes(t.value));
        }
    },
    methods: {
        loadData() {
            const clubId = this.$route.query.clubId ? parseInt(this.$route.query.clubId) : null;
            if (!clubId) {
                this.$showError('No club ID provided.');
                return;
            }
            this.clubId = clubId;
            const year = this.$route.query.year ? parseInt(this.$route.query.year, 10) : null;
            this.isLoading = true;

            Promise.all([
                svc.getAllMembershipTypes(),
                svc.getClubMembershipTypes(clubId, year)
            ]).then(([typesRes, clubRes]) => {
                this.isLoading = false;

                if (typesRes.body && typesRes.body.isSuccess) {
                    const types = typesRes.body.outputData || [];
                    this.availableTypes = types.map(t => ({
                        label: t.clubMembershipTypeLabel || t.clubMembershipType,
                        value: t.clubMembershipTypeId
                    }));
                }

                if (clubRes.body && clubRes.body.isSuccess) {
                    const output = clubRes.body.outputData || {};
                    const activeMemberships = output.activeMembershipTypes || [];
                    const requestedMemberships = output.requestedMembershipTypes || [];

                    this.approvedTypes = activeMemberships.map(m => ({
                        clubMembershipTypeId: m.clubMembershipTypeId,
                        name: m.clubMembershipTypeLabel || m.clubMembershipType,
                        price: m.price
                    }));

                    this.requestedTypes = requestedMemberships.map((m, idx) => ({
                        id: m.id || -(idx + 1),
                        clubMembershipTypeId: m.clubMembershipTypeId,
                        name: m.clubMembershipTypeLabel || m.clubMembershipType,
                        price: m.price
                    }));
                }
            }, () => {
                this.isLoading = false;
                this.$showError('Failed to load membership types. Please try again.');
            });
        },

        addAssignment() {
            if (!this.selectedTypeId) {
                this.$showError('Please select a membership type.');
                return;
            }
            if (this.newPrice === null || this.newPrice < 0) {
                this.$showError('Please enter a valid price.');
                return;
            }

            const selectedType = this.availableTypes.find(t => t.value === this.selectedTypeId);
            if (!selectedType) return;

            this.requestedTypes.push({
                id: -(this.requestedTypes.length + 1),
                clubMembershipTypeId: selectedType.value,
                name: selectedType.label,
                price: this.newPrice
            });

            this.selectedTypeId = null;
            this.newPrice = null;
        },

        removeAssignment(index) {
            this.requestedTypes.splice(index, 1);
        },

        addNewMembershipType() {
            const trimmedName = this.newMembershipTypeName ? this.newMembershipTypeName.trim() : '';

            if (!trimmedName) {
                this.$showError('Please enter a membership type name.');
                return;
            }

            const nameExists = this.availableTypes.some(
                t => t.label.toLowerCase() === trimmedName.toLowerCase()
            );

            if (nameExists) {
                this.$showError('A membership type with this name already exists.');
                return;
            }

            const tempId = -(this.availableTypes.length + 1000);
            this.availableTypes.push({
                label: trimmedName,
                value: tempId
            });

            this.selectedTypeId = tempId;
            this.newMembershipTypeName = '';
            this.$showSuccess('New membership type created. Set the price and click Assign.');
        },

        saveRequest(acknowledgedRemovals = false) {
            this.isSaving = true;
            const payload = this.requestedTypes.map(t => ({
                id: t.id || 0,
                clubMembershipTypeId: t.clubMembershipTypeId,
                clubMembershipTypeLabel: t.name || '',
                clubMembershipType: t.name || '',
                price: Number(t.price) || 0
            }));

            svc.updateClubMembershipTypes(this.clubId, payload, acknowledgedRemovals === true).then(response => {
                this.isSaving = false;
                if (response.body && response.body.isSuccess) {
                    const result = response.body.outputData;

                    if (result && result.status === 'ConfirmationRequired') {
                        this.confirmMembershipTypeRemovals(result.removedMembershipTypes);
                        return;
                    }

                    this.savedSuccessfully = true;
                } else {
                    this.$showError((response.body && response.body.message) || 'An error occurred. Please try again.');
                }
            }, () => {
                this.isSaving = false;
                this.$showError('An error occurred. Please try again.');
            });
        },

        confirmMembershipTypeRemovals(removedMembershipTypes) {
            this.removalWarning = buildMembershipTypeRemovalWarning(removedMembershipTypes);
            this.isRemovalWarningVisible = true;
        },

        cancelMembershipTypeRemovals() {
            this.isRemovalWarningVisible = false;
            this.removalWarning = null;
        },

        acceptMembershipTypeRemovals() {
            this.isRemovalWarningVisible = false;
            this.removalWarning = null;
            this.saveRequest(true);
        }
    },
    created() {
        this.loadData();
        EventBus.$emit('user-load-dashboard-card', 0);
    }
};



// WEBPACK FOOTER //
// ./src/components/user/dashboard/pages/clubs/membership-types/membership-types.js